import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice, getProductImage } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, count } = useCart();
  const shipping = subtotal >= 50 || subtotal === 0 ? 0 : 6.99;
  const tax = +(subtotal * 0.06).toFixed(2);
  const total = +(subtotal + shipping + tax).toFixed(2);
  const progress = Math.min(100, (subtotal / 50) * 100);

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8" data-testid="cart-empty">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
          <ShoppingBag size={32} />
        </div>
        <h1 className="font-display text-3xl font-bold text-brand-navy">Your cart is empty</h1>
        <p className="mt-3 text-slate-600">Time to elevate your collection.</p>
        <Link
          to="/shop"
          className="btn-primary mt-8 inline-flex rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide"
          data-testid="cart-empty-shop-link"
        >
          Browse the shop
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="cart-page">
      <h1 className="font-display text-3xl font-bold text-brand-navy sm:text-4xl">
        Your Cart <span className="text-slate-400">({count})</span>
      </h1>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr,380px]">
        <div className="space-y-4">
          {items.map((it) => (
            <div
              key={it.id}
              className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-4"
              data-testid={`cart-item-${it.id}`}
            >
              <img
                src={getProductImage(it)}
                alt={it.name}
                className="h-24 w-24 rounded-xl object-cover"
              />
              <div className="flex-1">
                <Link
                  to={`/product/${it.id}`}
                  className="line-clamp-2 font-semibold text-brand-navy hover:text-brand-teal"
                >
                  {it.name}
                </Link>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {it.category}
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="inline-flex items-center overflow-hidden rounded-full border border-slate-200">
                    <button
                      type="button"
                      onClick={() => updateQty(it.id, it.quantity - 1)}
                      className="p-2 hover:bg-slate-100"
                      aria-label="Decrease"
                      data-testid={`cart-decrease-${it.id}`}
                    >
                      <Minus size={12} />
                    </button>
                    <span className="min-w-[2ch] text-center text-sm font-bold text-brand-navy" data-testid={`cart-qty-${it.id}`}>
                      {it.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateQty(it.id, it.quantity + 1)}
                      className="p-2 hover:bg-slate-100"
                      aria-label="Increase"
                      data-testid={`cart-increase-${it.id}`}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <span className="font-display text-lg font-extrabold text-brand-navy" data-testid={`cart-line-total-${it.id}`}>
                    {formatPrice(it.price * it.quantity)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeItem(it.id)}
                className="self-start rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"
                aria-label={`Remove ${it.name}`}
                data-testid={`cart-remove-${it.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <aside className="sticky top-28 h-fit rounded-2xl border border-slate-200 bg-white p-6" data-testid="cart-summary">
          <h2 className="font-display text-xl font-bold text-brand-navy">Order Summary</h2>

          {shipping > 0 && (
            <div className="mt-4 rounded-xl bg-brand-teal/10 p-3 text-xs">
              <p className="font-semibold text-brand-navy">
                Add <span className="font-bold">{formatPrice(50 - subtotal)}</span> more for free shipping
              </p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white">
                <div className="h-full bg-brand-teal transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          <dl className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between text-slate-600">
              <dt>Subtotal</dt>
              <dd data-testid="cart-subtotal">{formatPrice(subtotal)}</dd>
            </div>
            <div className="flex justify-between text-slate-600">
              <dt>Shipping</dt>
              <dd>{shipping === 0 ? "Free" : formatPrice(shipping)}</dd>
            </div>
            <div className="flex justify-between text-slate-600">
              <dt>Tax (6%)</dt>
              <dd>{formatPrice(tax)}</dd>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-3 font-display text-lg font-bold text-brand-navy">
              <dt>Total</dt>
              <dd data-testid="cart-total">{formatPrice(total)}</dd>
            </div>
          </dl>

          <Link
            to="/checkout"
            className="btn-primary mt-6 flex w-full items-center justify-center rounded-full py-3 text-sm font-bold uppercase tracking-wide"
            data-testid="checkout-button"
          >
            Checkout with Square
          </Link>
          <Link
            to="/shop"
            className="mt-3 flex w-full items-center justify-center text-sm font-semibold text-brand-teal hover:text-brand-navy"
          >
            Continue shopping
          </Link>
        </aside>
      </div>
    </div>
  );
}
